import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LINES = [
    { text: "Hands-on physiotherapy", cls: "" },
    { text: "for people who want to get", cls: "" },
    { text: "back to work, sport, and", cls: "" },
    { text: "ordinary days.", cls: "font-cormorant italic font-medium text-ochre" },
];

const PILLS = [
    { label: "No referral required", id: "hero-pill-no-referral" },
    { label: "Seen within 7 days", id: "hero-pill-7-days" },
    { label: "Free parking", id: "hero-pill-parking" },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const yDetail = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 sm:pt-44">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <motion.p
                    data-testid="hero-eyebrow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-mutedink"
                >
                    Independent clinic — 412 Elm Street, next to King
                </motion.p>
                <h1
                    data-testid="hero-headline"
                    className="font-syne text-4xl font-bold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl"
                >
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${line.cls}`}
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.25 + i * 0.09, duration: 0.9, ease: EASE }}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-3"
                    data-testid="hero-pills"
                >
                    {PILLS.map((p) => (
                        <span
                            key={p.id}
                            data-testid={p.id}
                            className="border border-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-ink hover:text-bone"
                        >
                            {p.label}
                        </span>
                    ))}
                </motion.div>
            </div>

            <div className="mx-auto mt-16 grid max-w-7xl grid-cols-12 gap-4 px-5 pb-24 sm:px-8">
                <motion.figure
                    style={{ y: yMain }}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 1, ease: EASE }}
                    className="col-span-12 sm:col-span-7"
                    data-testid="hero-photo-main"
                >
                    <div className="border border-line bg-white p-2">
                        <img
                            src="https://images.unsplash.com/photo-1640746530069-1d413652cfd1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                            alt="Close-up of manual physiotherapy treatment"
                            className="aspect-[4/3] w-full object-cover grayscale-[35%]"
                            loading="eager"
                        />
                    </div>
                    <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">
                        Fig. 01 — Manual therapy, one clinician one patient
                    </figcaption>
                </motion.figure>
                <motion.figure
                    style={{ y: yDetail }}
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.15, duration: 1, ease: EASE }}
                    className="col-span-8 col-start-5 -mt-10 sm:col-span-4 sm:col-start-9 sm:mt-24"
                    data-testid="hero-photo-detail"
                >
                    <div className="border border-line bg-white p-2">
                        <img
                            src="https://images.pexels.com/photos/8219160/pexels-photo-8219160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt="Kinesiology taping during physical rehabilitation"
                            className="aspect-[3/4] w-full object-cover grayscale-[35%]"
                            loading="eager"
                        />
                    </div>
                    <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">
                        Fig. 02 — Sport taping &amp; rehab
                    </figcaption>
                </motion.figure>
            </div>
        </section>
    );
};
